import { useEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const NIVEAUX = ["Sous-sol", "Rez-de-chaussée", "Étage 1", "Étage 2", "Étage 3"];
const TYPES = ["Maison de chambres", "Maison", "Terrain", "Entrepôt", "Bureau"];
const VOCATIONS = ["Résidentielle", "Commerciale", "Mixte"];
const ANCIENNES_VOCATIONS = { "Résidentiel": "Résidentielle", "Commercial": "Commerciale" };
export const TAXABILITES = [
  { v: "tps_tvq", l: "Taxable TPS + TVQ" }, { v: "tps", l: "TPS seulement" }, { v: "tvq", l: "TVQ seulement" },
  { v: "mixte", l: "Mixte" }, { v: "non", l: "Non taxable" },
];
const AUTRE = "__autre__";
const SANS = "__aucun__";

export const ChoixAutre = ({ label, value, options, onCommit, testId }) => {
  const [autre, setAutre] = useState(!!value && !options.includes(value));
  const [txt, setTxt] = useState(value || "");
  useEffect(() => { setTxt(value || ""); setAutre(!!value && !options.includes(value)); }, [value, options]);
  return (
    <div className="space-y-1">
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      <Select value={autre ? AUTRE : value || undefined} onValueChange={(v) => { if (v === AUTRE) { setAutre(true); setTxt(""); } else { setAutre(false); onCommit(v); } }}>
        <SelectTrigger className="h-9 bg-white" data-testid={`${testId}-select`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          <SelectItem value={AUTRE} data-testid={`${testId}-autre`}>Autre…</SelectItem>
        </SelectContent>
      </Select>
      {autre && <Input value={txt} autoFocus maxLength={60} placeholder="Précisez" className="h-9" onChange={(e) => setTxt(e.target.value)}
        onBlur={() => txt.trim() !== (value || "") && onCommit(txt.trim())} data-testid={`${testId}-autre-input`} />}
    </div>
  );
};

const Puce = ({ actif, children, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${actif ? "border-emerald-700 bg-emerald-700 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-emerald-600"}`}>
    {children}
  </button>
);

const NiveauxChoix = ({ value, onCommit, testId }) => {
  const [txt, setTxt] = useState("");
  const toggle = (n) => onCommit(value.includes(n) ? value.filter((x) => x !== n) : [...value, n]);
  const ajouter = () => { const n = txt.trim(); if (n && !value.includes(n)) onCommit([...value, n]); setTxt(""); };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">Niveaux</Label>
      <div className="flex flex-wrap gap-1.5" data-testid={testId}>
        {NIVEAUX.map((n) => <Puce key={n} actif={value.includes(n)} onClick={() => toggle(n)} testId={`${testId}-${n}`}>{n}</Puce>)}
        {value.filter((n) => !NIVEAUX.includes(n)).map((n) => <Puce key={n} actif onClick={() => toggle(n)} testId={`${testId}-${n}`}>{n}<X className="h-3 w-3" /></Puce>)}
        <Input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ajouter()} onBlur={ajouter}
          maxLength={40} placeholder="Autre niveau" className="h-7 w-32 text-xs" data-testid={`${testId}-autre-input`} />
      </div>
    </div>
  );
};

export const TaxabiliteChoix = ({ value, onChange, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">Taxable</Label>
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {TAXABILITES.map((t) => <Puce key={t.v} actif={value === t.v} onClick={() => onChange(t.v)} testId={`${testId}-${t.v}`}>{t.l}</Puce>)}
    </div>
    {value === "mixte" && <p className="text-[11px] text-slate-500">Ex. : rez-de-chaussée commercial taxable, étage résidentiel non taxable. La TPS/TVQ est indiquée « à répartir » dans le commentaire.</p>}
  </div>
);

export const ImmeubleSelect = ({ value, immeubles, onChange, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">Immeuble</Label>
    <Select value={value || SANS} onValueChange={(v) => onChange(v === SANS ? "" : v)}>
      <SelectTrigger className="h-9 bg-white" data-testid={testId}><SelectValue placeholder="Choisir…" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={SANS}>— Aucun —</SelectItem>
        {immeubles.map((i) => <SelectItem key={i.id} value={i.nom}>{i.nom}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const depuisCfg = (cfg) => ({ immeuble: cfg?.immeuble || "", adresse: cfg?.adresse || "",
  taxabilite: cfg?.taxabilite || (cfg && cfg.taxable === false ? "non" : "tps_tvq"),
  niveaux: cfg?.niveaux || [], type: cfg?.type || "", vocation: ANCIENNES_VOCATIONS[cfg?.vocation] || cfg?.vocation || "", alias: cfg?.alias || [] });

const AliasChoix = ({ value, onCommit, testId }) => {
  const [txt, setTxt] = useState("");
  const ajouter = () => { const n = txt.trim(); if (n && !value.includes(n)) onCommit([...value, n]); setTxt(""); };
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">Noms d'annonce Airbnb / Booking</Label>
      <div className="flex flex-wrap gap-1.5" data-testid={testId}>
        {value.map((a) => <Puce key={a} actif onClick={() => onCommit(value.filter((x) => x !== a))} testId={`${testId}-${a}`}>{a}<X className="h-3 w-3" /></Puce>)}
        <Input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ajouter()} onBlur={ajouter}
          maxLength={160} placeholder="Ex. : Saint Germain (Étage 1)" className="h-7 w-56 text-xs" data-testid={`${testId}-input`} />
      </div>
      <p className="text-[11px] text-slate-500">Les réservations Airbnb/Booking importées sous ce nom sont rattachées à ce logement (les déjà importées aussi, dès l'ajout).</p>
    </div>
  );
};

export const LogementRow = ({ nom, cfg, existants = [], immeubles, onRenamed, onCfg, onDelete }) => {
  const [val, setVal] = useState(nom);
  const [f, setF] = useState(() => depuisCfg(cfg));
  const courant = useRef(f);
  const enregistre = useRef(depuisCfg(cfg));
  useEffect(() => { const d = depuisCfg(cfg); enregistre.current = d; courant.current = d; setF(d); }, [cfg]);
  const maj = (champs) => { courant.current = { ...courant.current, ...champs }; setF(courant.current); };
  const renommer = async () => {
    const nouveau = val.trim();
    if (!nouveau || nouveau === nom) { setVal(nom); return; }
    if (existants.includes(nouveau) && !window.confirm(`« ${nouveau} » existe déjà : fusionner « ${nom} » avec ce logement ?`)) { setVal(nom); return; }
    try { await api.post("/logements/renommer", { ancien: nom, nouveau }); toast.success(`Logement renommé « ${nouveau} »`); onRenamed(); }
    catch (e) { toast.error(errMsg(e)); setVal(nom); }
  };
  const save = (champs = {}) => { maj(champs); onCfg({ nom, ...courant.current }); };
  const saveTexte = (k) => courant.current[k] !== enregistre.current[k] && save();
  const id = `logement-${nom}`;
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`logement-row-${nom}`}>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div className="space-y-1"><Label className="text-xs text-slate-500">Nom du logement</Label>
          <Input value={val} maxLength={120} onChange={(e) => setVal(e.target.value)} onBlur={renommer} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            className="h-9" data-testid={`logement-input-${nom}`} /></div>
        <ImmeubleSelect value={f.immeuble} immeubles={immeubles} onChange={(immeuble) => save({ immeuble })} testId={`logement-immeuble-${nom}`} />
        {cfg && <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600" title="Supprimer la fiche du logement" onClick={() => onDelete(nom)} data-testid={`logement-supprimer-${nom}`}><X className="h-4 w-4" /></Button>}
      </div>
      <TaxabiliteChoix value={f.taxabilite} onChange={(taxabilite) => save({ taxabilite })} testId={`logement-taxable-${nom}`} />
      <div className="space-y-1"><Label className="text-xs text-slate-500">Adresse du logement</Label>
        <Input value={f.adresse} maxLength={200} onChange={(e) => maj({ adresse: e.target.value })} onBlur={() => saveTexte("adresse")} placeholder="Numéro, rue, ville" className="h-9" data-testid={`logement-adresse-${nom}`} /></div>
      <NiveauxChoix value={f.niveaux} onCommit={(niveaux) => save({ niveaux })} testId={`${id}-niveaux`} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ChoixAutre label="Type" value={f.type} options={TYPES} onCommit={(type) => save({ type })} testId={`${id}-type`} />
        <ChoixAutre label="Vocation" value={f.vocation} options={VOCATIONS} onCommit={(vocation) => save({ vocation })} testId={`${id}-vocation`} />
      </div>
      <AliasChoix value={f.alias} onCommit={(alias) => save({ alias })} testId={`${id}-alias`} />
    </div>
  );
};

export const NouveauLogement = ({ immeubles, onAdd, onCancel }) => {
  const [f, setF] = useState({ nom: "", immeuble: "", adresse: "", taxabilite: "tps_tvq" });
  return (
    <div className="space-y-3 rounded-md bg-emerald-50/60 p-3" data-testid="logement-nouveau">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div className="space-y-1"><Label className="text-xs text-slate-500">Nom du logement</Label>
          <Input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} placeholder="Ex. : Gîte Notre-Dame (RdC)" className="h-9 bg-white" data-testid="logement-nouveau-nom" /></div>
        <ImmeubleSelect value={f.immeuble} immeubles={immeubles} onChange={(immeuble) => setF({ ...f, immeuble })} testId="logement-nouveau-immeuble" />
        <div className="space-y-1"><Label className="text-xs text-slate-500">Adresse du logement</Label>
          <Input value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} placeholder="Numéro, rue, ville" className="h-9 bg-white" data-testid="logement-nouveau-adresse" /></div>
      </div>
      <TaxabiliteChoix value={f.taxabilite} onChange={(taxabilite) => setF({ ...f, taxabilite })} testId="logement-nouveau-taxable" />
      <div className="flex gap-1">
        <Button size="sm" className="h-9 bg-emerald-700 hover:bg-emerald-800" disabled={!f.nom.trim()} onClick={() => onAdd(f)} data-testid="logement-nouveau-ajouter"><Plus className="h-3.5 w-3.5 mr-1" />Ajouter</Button>
        <Button size="sm" variant="ghost" className="h-9" onClick={onCancel} data-testid="logement-nouveau-annuler">Annuler</Button>
      </div>
    </div>
  );
};
